import React, { useState } from "react"
import { DateRange, resolvePresetDateRange } from "../../utils/datePreset"
import dayjs from "dayjs"
import { Box, Button, Group, Text } from "@mantine/core"
import { DatePickerInput } from "@mantine/dates"
import { CalendarBlankIcon } from "@phosphor-icons/react"
import { DatePresetFilter } from "../DatePresetFilter"

interface Props {
  start: string
  end: string
  total: number
  snapshotDate: string
  selectedCellCount: number
  onChange: (start: string, end: string) => void
  onSnapshotDateChange: (snapshotDate: string) => void
}

// 集計時点の下限。これより前のスナップショットは存在しない
const SNAPSHOT_MIN_DATE = '2026-05-11'

const DateRangeFilter: React.FC<Props> = ({
  start,
  end,
  total,
  snapshotDate,
  selectedCellCount,
  onChange,
  onSnapshotDateChange,
}) => {
  // 既存の初期表示範囲（2年前～昨日）を維持するため custom + 受け取った初期値で開始する
  const [preset, setPreset] = useState('custom')
  const [dateRange, setDateRange] = useState<DateRange>([start, end])

  // 未来日は選択させない
  const today = dayjs().format('YYYY-MM-DD')

  // null のときはカスタムで from/to が未入力。送信ボタンの非活性判定にも使う
  const resolved = resolvePresetDateRange(preset, dateRange)

  const apply = () => {
    if (!resolved) return
    onChange(resolved[0], resolved[1])
  }

  return (
    <Box bg="#00000008" p={16} bdrs={8}>
      <Group gap={24} align="flex-start">
        <Text fz={13} fw={600} c="#000000CC" style={{ width: '100px', textAlign: 'start' }}>
          集計時点
        </Text>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <DatePickerInput
            valueFormat="YYYY.MM.DD"
            w={160}
            rightSection={<CalendarBlankIcon size={16} weight="fill" color="#00000099" />}
            rightSectionPointerEvents="none"
            value={snapshotDate}
            onChange={(v) => {
              // クリア操作は許可しない（必須項目）
              if (!v) return
              onSnapshotDateChange(v)
            }}
            minDate={SNAPSHOT_MIN_DATE}
            maxDate={today}
            locale="ja"
          />
          <Text fz={11} c="#00000099" mt={4}>
            ※選択日の前日までの集計データを表示します。集計データが無い日を選択した場合は、それ以前の最新の集計データを参照します。
          </Text>
        </Box>
      </Group>

      <Box mt={16}>
        <DatePresetFilter
          presetLabel="最終紹介日"
          value={preset}
          dateRange={dateRange}
          onChangeValue={setPreset}
          onChangeDateRange={setDateRange}
          maxDate={today}
        />
      </Box>

      <Group justify="space-between" mt={16}>
        <Group gap={24}>
          <Text fz={13} c="#000000CC">
            集計対象: <Text span fw={600}>{total.toLocaleString()}</Text> 人
          </Text>
          {selectedCellCount > 0 && (
            <Text fz={13} c="#000000CC">
              マトリクス選択中: <Text span fw={600}>{selectedCellCount}</Text> セル
            </Text>
          )}
        </Group>
        <Button
          variant="filled"
          radius="xl"
          fz={13}
          fw={600}
          w={120}
          onClick={apply}
          disabled={!resolved}
        >
          絞り込み表示
        </Button>
      </Group>
    </Box>
  )
}

export default DateRangeFilter
